import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Icon } from '@/common/components/Icon'
import Loader from '@/common/components/Loader'
import { Header, PageTitle } from '@/components'
import { Button, Card } from '@/components/ui'
import SelectField from '@/components/select-field'
import { IColumn, Table } from '@/core/components/Table'
import { useCheckPermission } from '@/core/hooks/use-check-permission'
import { BadgeDto, BadgeUpsertDto, PartnerDto } from '@/common/models/gamification'
import {
  createBadge,
  deleteBadge,
  getBadges,
  getPartners,
  updateBadge,
} from '@/core/services/gamification'

import { MessageBox } from '../messages/MessageBox'
import { CreateBadgeModal } from './CreateBadgeModal'

export const BadgesPage: React.FC = () => {
  const { editionId } = useParams()
  const { hasEditPermission } = useCheckPermission({ page: 'GAMIFICATION' })

  const [partners, setPartners] = useState<PartnerDto[]>([])
  const [badges, setBadges] = useState<BadgeDto[]>([])
  const [selectedPartnerId, setSelectedPartnerId] = useState('')
  const [loading, setLoading] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<BadgeDto | null>(null)

  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>()
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const handleError = (e: unknown) => {
    setIsError(true)
    setShowMessageBox(true)
    setMessage(e instanceof Error ? e.message : 'Something went wrong')
    setErrorStatus((e as { response?: { status?: number } })?.response?.status)
  }

  const notify = (msg: string) => {
    setIsError(false)
    setMessage(msg)
    setShowMessageBox(true)
  }

  const getPartnersData = useCallback(async () => {
    if (!editionId) return
    try {
      const { data } = await getPartners(editionId)
      setPartners(data ?? [])
    } catch (e) {
      handleError(e)
    }
  }, [editionId])

  const getBadgesData = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await getBadges(selectedPartnerId || undefined)
      setBadges(data ?? [])
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }, [selectedPartnerId])

  useEffect(() => {
    getPartnersData()
  }, [getPartnersData])

  useEffect(() => {
    getBadgesData()
  }, [getBadgesData])

  const partnerNameById = useMemo(
    () => new Map(partners.map((p) => [p.id, p.name])),
    [partners],
  )

  const partnerFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All partners' },
      ...partners.map((p) => ({ value: p.id, label: p.name })),
    ],
    [partners],
  )

  const openCreate = () => {
    setEditing(null)
    setCreateOpen(true)
  }

  const openEdit = (row: BadgeDto) => {
    if (!hasEditPermission) return
    setEditing(row)
    setCreateOpen(true)
  }

  const handleSubmit = async (values: BadgeUpsertDto, id?: string) => {
    try {
      setLoading(true)
      if (id) {
        await updateBadge(id, values)
        notify('Badge updated successfully')
      } else {
        await createBadge(values)
        notify('Badge created successfully')
      }
      await getBadgesData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      setLoading(true)
      await deleteBadge(id)
      notify('Badge deleted successfully')
      await getBadgesData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: 'Name',
        accessor: 'name',
        render: (row: BadgeDto) => (
          <span className="text-monochrome-1">{row.name}</span>
        ),
      },
      {
        title: 'Partner',
        accessor: 'partnerId',
        render: (row: BadgeDto) => (
          <span className="text-monochrome-2">
            {(row.partnerId && partnerNameById.get(row.partnerId)) || '—'}
          </span>
        ),
      },
      {
        title: 'Icon Ref',
        accessor: 'iconRef',
        render: (row: BadgeDto) => (
          <span className="text-monochrome-2">{row.iconRef || '—'}</span>
        ),
      },
      {
        title: 'Edit',
        accessor: '',
        render: (row: BadgeDto) => (
          <a
            className="view text-accent-1 underline"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              openEdit(row)
            }}
          >
            Edit
          </a>
        ),
      },
    ],
    [hasEditPermission, partnerNameById],
  )

  return (
    <>
      <Header
        pageTitle="Gamification"
        actions={
          hasEditPermission && (
            <Button variant="primary-bordered" onClick={openCreate}>
              Add Badge
              <Icon icon="plus-circle" width="18" height="18" viewBox="0 0 18 18" />
            </Button>
          )
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <div className="mb-5 flex items-center justify-between gap-4">
          <PageTitle icon="events-star" title="Badges" />
          <div className="w-64">
            <SelectField
              placeholder="All partners"
              options={partnerFilterOptions}
              value={selectedPartnerId || 'all'}
              onValueChange={(v) => setSelectedPartnerId(v === 'all' ? '' : v)}
            />
          </div>
        </div>
        <Table
          className="[&_th]:!whitespace-nowrap"
          columns={columns}
          data={badges}
          noDataMessage="No badges yet"
        />
      </Card>

      <CreateBadgeModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        badge={editing}
        partners={partners}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
      />

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
